import React from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import {
  findDOMNode,
  render,
  unmountComponentAtNode
} from 'react-dom';

import './ClickableTooltip.css';

let mountedPortals = [];

function limitPortals() {
  if (mountedPortals.length > 1) {
    for (let i = 0; i < mountedPortals.length - 1; i++) {
      mountedPortals[i].forceDismiss();
    }
  }
}

export default class ClickableTooltip extends React.Component {
  static propTypes = {
    text: PropTypes.string,
    placement: PropTypes.string, // TODO: add support for this
    content: PropTypes.node,
    children: PropTypes.node,
    onClick: PropTypes.func,
    style: PropTypes.object,
    checkScrollWidth: PropTypes.bool,
    truncate: PropTypes.bool,
    noArrow: PropTypes.bool,
    offset: PropTypes.number
  };

  static defaultProps = {
    placement: 'TOP',
    style: {},
    truncate: false,
    noArrow: false,
    offset: 6
  }

  constructor(props) {
    super(props);

    this._isMounted = false;
    this._clickable = false;
    this._dismissTimer = null;
    this._positionTimer = null;

    this._div = document.createElement('div');
    this._div.setAttribute('name', 'ClickableTooltip');
    this._div.setAttribute('data-placement', props.placement);

    if (props.onClick) {
      this._clickable = true;
      this._div.addEventListener('click', this.onClick);
      this._div.classList.add('clickable');
    }

    if (props.truncate) {
      this._div.classList.add('truncate');
    }

    if (props.noArrow) {
      this._div.classList.add('no-arrow');
    }

    for (let key in props.style) {
      this._div.style[key] = props.style[key];
    }
  }

  componentDidMount() {
    const node = findDOMNode(this);
    node.addEventListener('mouseenter', this.onMouseEnter);
    node.addEventListener('mouseleave', this.onMouseLeave);
  }

  componentWillUnmount() {
    const node = findDOMNode(this);
    node.removeEventListener('mouseenter', this.onMouseEnter);
    node.removeEventListener('mouseleave', this.onMouseLeave);
    this.unmountPortal();
  }

  mountPortal = (props) => {
    if (this._isMounted) {
      return;
    }

    const { content } = props;

    if (!content) {
      return;
    }

    let node;
    if (React.isValidElement(content)) {
      node = content;
    } else if (typeof content === 'string' && content.length > 0) {
      node = <span>{content}</span>;
    } else {
      // Maybe empty string. Maybe null. Maybe something else. Don't render.
      return;
    }

    document.addEventListener('mousemove', this.onMouseMove);
    document.body.appendChild(this._div);
    render(node, this._div);
    this._isMounted = true;
    mountedPortals.push(this);
  }

  unmountPortal = () => {
    if (!this._isMounted) {
      return;
    }

    document.removeEventListener('mousemove', this.onMouseMove);
    unmountComponentAtNode(this._div);
    document.body.removeChild(this._div);
    this._isMounted = false;
    mountedPortals.splice(mountedPortals.indexOf(this), 1);
  }

  onMouseEnter = (e) => {
    if (this.props.checkScrollWidth && !this.isTooltipNecessary()) {
      return;
    }

    limitPortals();
    this.mountPortal(this.props);

    clearInterval(this._dismissTimer);
    clearTimeout(this._positionTimer);

    this._div.style.visibility = '';
    this._div.style.opacity = '1';
    this.setPosition(e.target.getBoundingClientRect());
  }

  onMouseLeave = () => {
    if (this._clickable) {
      this._dismissTimer = setInterval(this.maybeDismiss, 350);
    } else {
      this.dismiss();
    }
  }

  onMouseMove = (e) => {
    this._lastTarget = e.target;
  }

  onClick = (e) => {
    this.dismiss();
    this.props.onClick(e);
  }

  maybeDismiss = () => {
    const nodes = $(this._lastTarget).parents().get();
    if (nodes.indexOf(findDOMNode(this)) > -1 || nodes.indexOf(this._div) > -1) {
      return;
    }

    this.dismiss();
  }

  dismiss = () => {
    clearInterval(this._dismissTimer);
    this._div.style.opacity = '0';
    this._positionTimer = setTimeout(() => {
      this.resetPosition();
      this.unmountPortal();
    }, 500);
  }

  forceDismiss = () => {
    this._div.style.visibility = 'hidden';
    this._div.style.opacity = '0';
    this.resetPosition();
    this.unmountPortal();
  }

  setPosition = (rect) => {
    const {
      placement,
      offset,
      noArrow
    } = this.props;

    let left = rect.left + rect.width / 2 - this._div.clientWidth / 2;
    if (!noArrow && (left < 0 || left > window.innerWidth - this._div.clientWidth)) {
      this._div.classList.add('no-arrow');
    }

    left = Math.max(left, 0);
    left = Math.min(left, window.innerWidth - this._div.clientWidth);
    this._div.style.left = `${left}px`;

    let top;
    switch (placement) {
      case 'TOP':
        top = rect.top - this._div.clientHeight - 6;
        break;
      case 'BOTTOM':
        top = rect.bottom + offset;
        break;
    }

    this._div.style.top = `${top}px`;
  }

  resetPosition = () => {
    this._div.style.top = '-100px';
    this._div.style.left = '-100px';
  }

  isTooltipNecessary = () => {
    const node = findDOMNode(this);
    return node.scrollWidth > node.offsetWidth;
  }

  render() {
    return this.props.children;
  }
}
